import {
  Box,
  BoxProps,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Text,
  ButtonGroup,
  IconButton,
  InputRightElement,
  useColorModeValue,
  Select,
} from "@chakra-ui/react";
import { FormEvent, ReactNode, useEffect, useState } from "react";

import { FaSearch } from "react-icons/fa";

import { IoGrid } from "react-icons/io5";
import { BsFillGrid3X3GapFill } from "react-icons/bs";
import {
  FilterDataHandler,
  FilterDispatch,
  GridSizeProps,
  SortDataHandler,
  SortItemProps,
} from "utils/interface";
import { RiCloseFill } from "react-icons/ri";
import { useParameterStore } from "utils/context";

interface FilterUtilityProps extends FilterDispatch {
  placeholder?: string;
}

interface SearchUtilityProps extends BoxProps {
  value: string | number;
  handler: (value: string) => void;
}

export const SearchInputUtility = (props: SearchUtilityProps) => {
  const { value, handler, ...otherProps } = props;

  return (
    <Box {...otherProps}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon as={FaSearch} />
        </InputLeftElement>
        <Input
          aria-autocomplete={"both"}
          value={value}
          onChange={(e) => handler(e.target.value)}
          type={"text"}
          placeholder={"Search name..."}
          name={"search-input"}
          variant={"main"}
        />
        <InputRightElement>
          <IconButton
            aria-label="Delete search input"
            hidden={value ? false : true}
            display={"flex"}
            fontSize={"md"}
            size={"xs"}
            icon={<RiCloseFill />}
            variant={"unstyled"}
            onClick={() => handler("")}
          />
        </InputRightElement>
      </InputGroup>
    </Box>
  );
};

export const FilterUtility = ({
  filterValue,
  filterHandler,
  placeholder,
}: FilterUtilityProps) => {
  return (
    <Box width={{ base: "full", md: "auto" }}>
      {filterValue &&
        filterValue.dataFilter &&
        filterValue.dataFilter?.length > 0 && (
          <InputGroup>
            <Select
              variant={"main"}
              name={"filter-input"}
              textTransform={"capitalize"}
              minW={{ base: "200px", md: "xs" }}
              value={filterValue?.value ?? ""}
              placeholder={placeholder ?? undefined}
              onChange={(e) => {
                filterHandler &&
                  filterHandler((s) => ({ ...s, value: e.target.value }));
              }}
            >
              {filterValue?.dataFilter &&
                filterValue?.dataFilter.map((list, x) => (
                  <option key={x} value={list}>
                    Filtered: {list.toLowerCase()}
                  </option>
                ))}
            </Select>
          </InputGroup>
        )}
    </Box>
  );
};

export const SortUtility = (props: SortItemProps) => {
  const { valueData, handler, placeholder, options, ...selectProps } = props;
  return (
    <Box minW={"max-content"} flexGrow={1}>
      <Select
        {...selectProps}
        variant="main"
        value={valueData ?? undefined}
        onChange={(e) => {
          handler && handler(e.target.value);
        }}
        placeholder={placeholder ?? "Sort by"}
        textTransform={"capitalize"}
        mr={{ base: 4, md: 12 }}
      >
        {options !== undefined &&
          options.map((option, key) => (
            <option key={key} value={`${option.field} ${option.direction}`}>
              {option.label?.toLowerCase()}
            </option>
          ))}
      </Select>
    </Box>
  );
};

export const GridSizeUtility = () => {
  const filterButtonColor = useColorModeValue("gray.200", "main.spaceCadet");
  const notActiveColor = useColorModeValue("gray.200", "gray.100");
  const activeColor = useColorModeValue("main.blue", "main.blue");

  const { gridSize, setGridSize } = useParameterStore();

  return (
    <ButtonGroup
      spacing={0}
      borderWidth={3}
      boxSizing={"content-box"}
      borderColor={filterButtonColor}
      rounded={"lg"}
    >
      <IconButton
        roundedRight={"none"}
        variant={"ghost"}
        boxSize={9}
        bg={gridSize == "medium" ? filterButtonColor : "transparent"}
        _hover={{
          bg: filterButtonColor,
        }}
        color={gridSize == "medium" ? activeColor : notActiveColor}
        onClick={() => (setGridSize ? setGridSize("medium") : {})}
        aria-label="Medium Grid"
        icon={<IoGrid />}
      />
      <IconButton
        roundedLeft={"none"}
        variant={"ghost"}
        boxSize={9}
        bg={gridSize == "small" ? filterButtonColor : "transparent"}
        _hover={{
          bg: filterButtonColor,
        }}
        color={gridSize == "small" ? activeColor : notActiveColor}
        onClick={() => (setGridSize ? setGridSize("small") : {})}
        aria-label="Small Grid"
        icon={<BsFillGrid3X3GapFill />}
      />
    </ButtonGroup>
  );
};

export const DataDisplayUtilityBox = (props: { children?: ReactNode }) => {
  return (
    <Flex
      w={"full"}
      display={"flex"}
      flexWrap={{ base: "wrap", md: "nowrap" }}
      alignItems={"center"}
      justifyContent={{ base: "flex-end", md: "flex-end" }}
      gap={{ base: 2, md: 4 }}
      rowGap={{ base: 4, md: 0 }}
    >
      {props.children}
    </Flex>
  );
};
